import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'

// Components
import { GridCol, GridRow } from '../grid'
import Text from '../Text'
import Input from '../formControl/Input'
import { renderCard } from '../card/RequestListCard'
import Button from '../Button'
import { Row } from '../layout/LayoutAtoms'
import { Icon } from '../icon'

// Utils
import { useAppState } from '../../context/AppContext'

// Styles
import { mqFrom } from '../../styles/mediaQueries'
import { shadows } from '../../styles/effects'

// Types
import { IconType } from '../icon/IconType'

interface IProps {
  name: string
  setName: React.Dispatch<React.SetStateAction<string>>
  finalize: () => Promise<void>
  disableButton: boolean
}

const blocks = [
  {
    icon: 'Lock',
    type: 'goals',
  },
  {
    icon: 'List',
    type: 'collections',
  },
]

const StartConfirmation: React.FC<IProps> = ({
  name,
  setName,
  finalize,
  disableButton,
}) => {
  const { company, theme } = useAppState()
  const history = useHistory()
  const { t } = useTranslation()

  return (
    <GridRow alignType="center">
      <GridCol xs={12}>
        <Text textStyle="displayS" color="textLight" marginTop={12}>
          {t(`onboardingCreation.almostThere`)}
        </Text>
        <Text textStyle="displayM" marginTop={12}>
          {t(`evaluationModal.titles.evaluationAssessorDetails`)}
        </Text>
        <Text textStyle="displayS" marginTop={48} marginBottom={24}>
          {t(`termEvaluationsProgress.finalizeModal.evaluationName`)}
        </Text>
        <Card>
          <div>
            <StyledInput
              label={t(`termEvaluationsProgress.finalizeModal.inputTitle`)}
              additionalLabel={t('termEvaluationsProgress.renameSubtitle')}
              value={name}
              onChange={(event) => {
                setName(event.currentTarget.value)
              }}
            />
            <Text textStyle="caption" color="textLight">
              {t(`termEvaluationsProgress.finalizeModal.exampleText`, {
                year: new Date().getFullYear(),
              })}
            </Text>
          </div>
          <Preview>
            <Text textStyle="labelTinyCaps" marginBottom={8}>
              {t(`termEvaluationsProgress.finalizeModal.preview`)}
            </Text>
            {renderCard(
              {
                title: t('requests.requestCompleteAssessments', { name }),
                label: t('requests.daysLeft', { count: 14 }),
                completed: true,
                imgSrc: company?.logo,
              },
              1
            )}
          </Preview>
        </Card>
        <CheckCard>
          <StyledIcon type="Warning" color={theme.colors.primary} size={20} />
          <Text textStyle="labelHeavy" marginTop={16} color="primary">
            {t(`termEvaluationsProgress.finalizeModal.subtitle`)}
          </Text>
          <Text
            textStyle="label"
            marginTop={8}
            marginBottom={16}
            color="primary"
          >
            {t(`termEvaluationsProgress.finalizeModal.intro`)}
          </Text>
          <SettingsButton
            colorVariant="secondary"
            caption={`${t(`general.evaluation_other`)} ${t(
              'general.settings'
            ).toLowerCase()}`}
            onClick={() =>
              history.push('/company/settings/evaluations', { from: 'review' })
            }
          />
          <Button
            disabled={name.length < 2 || disableButton}
            colorVariant="primary"
            caption={t(`termEvaluationsProgress.start`)}
            onClick={finalize}
          />
          <Text
            textStyle="bodySmall"
            textAlign="center"
            marginTop={16}
            color="primaryFadeHalf"
          >
            {t(`termEvaluationsProgress.finalizeModal.requests`)}
          </Text>
        </CheckCard>
        <Text textStyle="displayS" marginBottom={24}>
          {t(`termEvaluationsProgress.finalizeModal.actionDescription`)}
        </Text>
        <Blocks>
          {blocks.map((block, index) => (
            <Block key={`confirm-block-${index}`}>
              <StyledIcon
                type={block.icon as IconType}
                color={theme.colors.textLight}
                size={24}
              />
              <Text textStyle="title" textAlign="center" marginTop={8}>
                {t(`termEvaluationsProgress.finalizeModal.${block.type}Title`)}
              </Text>
              <Text textStyle="bodySmall" textAlign="center" marginTop={8}>
                {t(`termEvaluationsProgress.finalizeModal.${block.type}Text`)}
              </Text>
            </Block>
          ))}
        </Blocks>
      </GridCol>
    </GridRow>
  )
}

export default StartConfirmation

const Card = styled.div`
  box-shadow: ${shadows.depthTwo};
  background-color: ${({ theme }) => theme.colors.lightest};
  padding: 24px;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  margin-bottom: 48px;

  ${mqFrom.M`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    > div {
      width: calc(50% - 24px);
    }
  `}
`

const Preview = styled.div`
  background-color: ${({ theme }) => theme.colors.primaryLight};
  padding: 12px;
  border-radius: 12px;
  margin-top: 16px;

  > li {
    box-shadow: ${shadows.depthTwo};
  }

  ${mqFrom.M`
    margin-top: 0;
  `}
`

const CheckCard = styled.div`
  background-color: ${({ theme }) => theme.colors.primaryFadeLight};
  padding: 24px 16px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 48px 0;
`

const SettingsButton = styled(Button)`
  margin-bottom: 8px;
`

const StyledInput = styled(Input)`
  margin-bottom: 8px;

  input {
    background-color: ${({ theme }) => theme.colors.textLightest};
    border-color: ${({ theme }) => theme.colors.textLightest};
  }

  label,
  p {
    color: ${({ theme }) => theme.colors.text};
  }
`

const Blocks = styled(Row)`
  align-items: stretch;
  flex-wrap: wrap;
`

const Block = styled.div`
  background-color: ${({ theme }) => theme.colors.textLightest};
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 16px;
  align-items: center;
  width: 100%;

  ${mqFrom.M`
    width: calc(50% - 8px);
  `}
`

const StyledIcon = styled(Icon)`
  margin: 0;
`
